import * as React from 'react'

import { Box, Icon, Section, Title } from '@factorialco/deprecated-design-system'
import { AttendanceOvertimePool, PayrollCompensationDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import PayrollCompensation from 'modules/attendance/components/TimeTrackingSettings/PayrollCompensation'
// eslint-disable-next-line factorial/no-allowed-mobx-collections
import timeoffCompensations, {
  TimeoffCompensationsProvider,
} from 'modules/attendance/store/collections/timeoffCompensation'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import type BalanceCarryover from 'modules/attendance/store/models/BalanceCarryover'
import TimeoffCompensation from 'modules/timeoff/components/TimeoffCompensation'
import TimeoffCompensationForm from 'modules/timeoff/lib/TimeoffCompensationForm'

import CompensationPeriodCard from '../CompensationPeriodCard'
import CreateOvertimeAllowanceModal from '../CreateOvertimeAllowanceModal'

type TitleProps = React.ComponentProps<typeof Title>

export interface Props {
  attendanceSetting: AttendanceSetting
  timeoffCompensationEnabled: boolean
  balanceCarryover?: BalanceCarryover
  setBalanceCarryover?: (balanceCarryover: BalanceCarryover) => void
  hasExtraHourRules: boolean
  hasTimeOffExtraHourRules: boolean
  overtimePools: AttendanceOvertimePool[]
  hasLimitBalancePool: boolean
}

const OvertimeCompensation = ({
  attendanceSetting,
  timeoffCompensationEnabled,
  balanceCarryover,
  setBalanceCarryover,
  hasExtraHourRules,
  hasTimeOffExtraHourRules,
  overtimePools,
  hasLimitBalancePool,
}: Props) => {
  const [createAllowanceModalOpen, setCreateAllowanceModalOpen] = React.useState(false)
  const settingId = attendanceSetting.attributes.id

  const descriptionLink: TitleProps['link'] = {
    href: helpCenterUrl('overtime_compensation_with_time_off'),
    label: i18n.t('time_tracking.settings.overtime.compensation.description_link'),
  }

  useAsyncData(
    () =>
      Promise.all([
        timeoffCompensations.fetch({
          data: {
            attendance_employees_setting_id: settingId,
          },
        }),
      ]),
    [settingId]
  )

  const { data } = useQuery(PayrollCompensationDocument, {
    variables: {
      attendanceEmployeesSettingId: settingId,
    },
    skip: hasExtraHourRules,
  })

  const payrollCompensations = data?.attendance.payrollCompensationsConnection.nodes ?? []

  const showTimeOffSettings = hasExtraHourRules
    ? hasTimeOffExtraHourRules && timeoffCompensationEnabled
    : timeoffCompensationEnabled
  const currentPayrollCompensation = payrollCompensations.at(-1)
  const currentTimeoffCompensation = timeoffCompensations.last()
  const formValues = currentTimeoffCompensation && currentTimeoffCompensation.toJS()
  const compensationForm = new TimeoffCompensationForm(formValues)
  const actionDisabled = !timeoffCompensationEnabled ? true : !currentTimeoffCompensation?.isEnabled

  const createAllowanceAction = timeoffCompensationEnabled
    ? {
        onClick: () => setCreateAllowanceModalOpen(true),
        disabled: actionDisabled,
        label: i18n.t('time_tracking.settings.overtime.compensation.timeoff.create_allowance'),
      }
    : undefined

  const handleModalOnSubmit = () => {
    setCreateAllowanceModalOpen(false)
  }

  let sectionData: {
    illustration?: React.ComponentProps<typeof Icon.Large>['icon']
    title: string
    description: string
    link?: TitleProps['link']
    action?: {
      onClick: () => void
      disabled: boolean
      label: string
    }
  } = {
    illustration: 'holidays-event-tenure',
    title: i18n.t('time_tracking.settings.overtime.compensation.title'),
    description: i18n.t('time_tracking.settings.overtime.compensation.description'),
    link: descriptionLink,
    action: createAllowanceAction,
  }

  if (hasExtraHourRules) {
    sectionData = {
      illustration: 'monety-bag',
      title: i18n.t('time_tracking.settings.compensation.title'),
      description: i18n.t('time_tracking.settings.compensation.description'),
      link: undefined,
      action: undefined,
    }
  }

  return (
    <Section
      illustration={sectionData.illustration}
      title={sectionData.title}
      description={sectionData.description}
      link={sectionData.link}
      action={sectionData.action}
    >
      <Box gap="s16">
        {hasExtraHourRules && balanceCarryover && (
          <CompensationPeriodCard
            attendanceSetting={attendanceSetting}
            balanceCarryover={balanceCarryover}
            overtimePools={overtimePools}
          />
        )}
        {showTimeOffSettings && (
          <TimeoffCompensationsProvider collection={timeoffCompensations}>
            <TimeoffCompensation
              attendanceSetting={attendanceSetting}
              compensationForm={compensationForm}
              balanceCarryover={balanceCarryover}
              setBalanceCarryover={setBalanceCarryover}
              hasExtraHourRules={hasExtraHourRules}
              hasLimitBalancePool={hasLimitBalancePool}
            />
            {createAllowanceModalOpen && (
              <CreateOvertimeAllowanceModal
                onSubmit={handleModalOnSubmit}
                onClose={() => setCreateAllowanceModalOpen(false)}
              />
            )}
          </TimeoffCompensationsProvider>
        )}

        {!hasExtraHourRules && (
          <PayrollCompensation
            settingId={settingId}
            payrollCompensation={currentPayrollCompensation}
          />
        )}
      </Box>
    </Section>
  )
}

export default observer(OvertimeCompensation)
